import { Formik } from 'formik';
import { useCallback } from 'react';
import { object } from 'yup';

import type {
  Countersink,
  ToleranceType,
  Value,
  Tolerance,
} from '@frontend/api';
import { RUNES_CALLOUT_TYPES, TOLERANCE_TYPES } from '@frontend/api';
import {
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  GDTSymbolText,
  Flex,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { MultiplierInput } from './shared/MultiplierInput';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import { ToleranceInputGroup } from './shared/ToleranceInputGroup';
import {
  createRequiredStringNumberField,
  createBooleanField,
  createStringToleranceValidationGroup,
  createMultiplierValidationGroup,
  createStringAngleValueValidation,
  createStringAngleToleranceValidationGroup,
  parsePrecisionValue,
} from './shared/validation';
import {
  buildPrecisionMeasurementObject,
  stringToleranceFormValuesToConfig,
  extractMultiplier,
} from './shared/valueBuilders';

type CountersinkFormValues = {
  diameter: string;
  hasTolerance: boolean;
  toleranceType: ToleranceType;
  toleranceUpper?: string;
  toleranceLower?: string;
  toleranceSymmetric?: string;
  hasMult: boolean;
  mult?: number;
  hasAngle: boolean;
  angleValue?: string;
  angleHasTolerance: boolean;
  angleToleranceType: ToleranceType;
  angleToleranceUpper?: string;
  angleToleranceLower?: string;
  angleToleranceSymmetric?: string;
};

const initialValues: CountersinkFormValues = {
  diameter: '0',
  hasTolerance: false,
  toleranceType: TOLERANCE_TYPES.SYMMETRIC,
  toleranceUpper: undefined,
  toleranceLower: undefined,
  toleranceSymmetric: undefined,
  hasMult: false,
  mult: undefined,
  hasAngle: false,
  angleValue: undefined,
  angleHasTolerance: false,
  angleToleranceType: TOLERANCE_TYPES.SYMMETRIC,
  angleToleranceUpper: undefined,
  angleToleranceLower: undefined,
  angleToleranceSymmetric: undefined,
};

/**
 * Converts CountersinkFormValues into a concise string summary using appropriate symbols
 * @param values - The countersink form values to convert
 * @returns A formatted string representation of the countersink
 */
const formatCountersinkSummary = (values: CountersinkFormValues): string => {
  let summary = '';

  // Add multiplier first if present
  if (values.hasMult && values.mult !== undefined) {
    summary += `${values.mult}×`;
  }

  // Add countersink symbol and diameter
  summary += `⌵${values.diameter}`;

  // Add diameter tolerance if present
  if (values.hasTolerance) {
    switch (values.toleranceType) {
      case TOLERANCE_TYPES.SYMMETRIC:
        if (values.toleranceSymmetric) {
          summary += ` ±${values.toleranceSymmetric}`;
        }
        break;
      case TOLERANCE_TYPES.ASYMMETRIC:
        if (values.toleranceUpper && values.toleranceLower) {
          summary += ` +${values.toleranceUpper}/-${values.toleranceLower}`;
        }
        break;
      case TOLERANCE_TYPES.LIMIT:
        if (values.toleranceUpper && values.toleranceLower) {
          summary += ` [${values.toleranceLower}, ${values.toleranceUpper}]`;
        }
        break;
    }
  }

  // Add angle information if present
  if (values.hasAngle && values.angleValue) {
    summary += ` ∠${values.angleValue}°`;

    // Add angle tolerance if present
    if (values.angleHasTolerance) {
      switch (values.angleToleranceType) {
        case TOLERANCE_TYPES.SYMMETRIC:
          if (values.angleToleranceSymmetric) {
            summary += ` ±${values.angleToleranceSymmetric}°`;
          }
          break;
        case TOLERANCE_TYPES.ASYMMETRIC:
          if (values.angleToleranceUpper && values.angleToleranceLower) {
            summary += ` +${values.angleToleranceUpper}°/-${values.angleToleranceLower}°`;
          }
          break;
        case TOLERANCE_TYPES.LIMIT:
          if (values.angleToleranceUpper && values.angleToleranceLower) {
            summary += ` [${values.angleToleranceLower}°, ${values.angleToleranceUpper}°]`;
          }
          break;
      }
    }
  }

  return summary;
};

const validationSchema = object().shape({
  diameter: createRequiredStringNumberField('Diameter'),
  ...createStringToleranceValidationGroup(),
  ...createMultiplierValidationGroup(),
  hasAngle: createBooleanField(),
  angleValue: createStringAngleValueValidation(),
  ...createStringAngleToleranceValidationGroup(),
});

export const CountersinkForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: CountersinkFormValues) => {
      // Parse diameter value for tolerance calculations
      const parsedDiameter = parsePrecisionValue(values.diameter);
      if (!parsedDiameter) {
        return;
      }

      // Build diameter measurement with tolerance
      const diameterToleranceConfig = stringToleranceFormValuesToConfig(
        {
          hasTolerance: values.hasTolerance,
          toleranceType: values.toleranceType,
          toleranceSymmetric: values.toleranceSymmetric,
          toleranceUpper: values.toleranceUpper,
          toleranceLower: values.toleranceLower,
        },
        parsedDiameter.numericValue,
      );

      const dia = buildPrecisionMeasurementObject(
        values.diameter,
        diameterToleranceConfig,
      );
      if (!dia) {
        return;
      }

      // Build angle measurement if enabled
      let angle: { value: Value; tol?: Tolerance } | undefined;
      if (values.hasAngle && values.angleValue) {
        const parsedAngle = parsePrecisionValue(values.angleValue);
        if (!parsedAngle) {
          return;
        }

        const angleToleranceConfig = stringToleranceFormValuesToConfig(
          {
            hasTolerance: values.angleHasTolerance,
            toleranceType: values.angleToleranceType,
            toleranceSymmetric: values.angleToleranceSymmetric,
            toleranceUpper: values.angleToleranceUpper,
            toleranceLower: values.angleToleranceLower,
          },
          parsedAngle.numericValue,
        );

        angle = buildPrecisionMeasurementObject(
          values.angleValue,
          angleToleranceConfig,
        );
        if (!angle) {
          return;
        }
      }

      // Build the final Countersink object
      const countersink: Countersink = {
        calloutType: RUNES_CALLOUT_TYPES.COUNTERSINK,
        mult: extractMultiplier({ hasMult: values.hasMult, mult: values.mult }),
        dia,
        angle,
      };

      onSubmit(countersink, formatCountersinkSummary(values));
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => (
        <SubFormWrapper
          isValid={isValid}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
          renderSelectedType={renderSelectedType}
          submitLabel="Add Countersink"
        >
          {/* Preview Section */}
          <FormPreviewSection>
            <GDTSymbolText size="md">
              {formatCountersinkSummary(values)}
            </GDTSymbolText>
          </FormPreviewSection>

          {/* Form Fields */}
          <Flex direction="column" gap={1} px={1} pb={1}>
            {/* Diameter Value */}
            <FormControl>
              <FormLabel>Diameter</FormLabel>
              <NumberValueField
                name="diameter"
                value={values.diameter}
                onChange={(value) => setFieldValue('diameter', value)}
                size="sm"
                min={0}
              />
              {errors.diameter && touched.diameter && (
                <FormErrorMessage>{errors.diameter}</FormErrorMessage>
              )}
            </FormControl>

            {/* Diameter Tolerance */}
            <ToleranceInputGroup
              hasToleranceFieldName="hasTolerance"
              hasTolerance={values.hasTolerance}
              onHasToleranceChange={(checked) =>
                setFieldValue('hasTolerance', checked)
              }
              toleranceTypeFieldName="toleranceType"
              toleranceType={values.toleranceType}
              onToleranceTypeChange={(type) =>
                setFieldValue('toleranceType', type)
              }
              symmetricFieldName="toleranceSymmetric"
              symmetricValue={values.toleranceSymmetric}
              onSymmetricChange={(value) =>
                setFieldValue('toleranceSymmetric', value)
              }
              upperFieldName="toleranceUpper"
              upperValue={values.toleranceUpper}
              onUpperChange={(value) => setFieldValue('toleranceUpper', value)}
              lowerFieldName="toleranceLower"
              lowerValue={values.toleranceLower}
              onLowerChange={(value) => setFieldValue('toleranceLower', value)}
              errors={errors}
              touched={touched}
              checkboxLabel="Include diameter tolerance"
            />

            {/* Angle Checkbox and Inputs */}
            <Checkbox
              name="hasAngle"
              isChecked={values.hasAngle}
              onChange={(e) => setFieldValue('hasAngle', e.target.checked)}
              size="sm"
            >
              Include angle
            </Checkbox>
            {values.hasAngle && (
              <>
                <FormControl>
                  <FormLabel>Angle Value (degrees)</FormLabel>
                  <NumberValueField
                    name="angleValue"
                    value={values.angleValue || ''}
                    onChange={(value) => setFieldValue('angleValue', value)}
                    size="sm"
                    min={0}
                    max={360}
                  />
                  {errors.angleValue && touched.angleValue && (
                    <FormErrorMessage>{errors.angleValue}</FormErrorMessage>
                  )}
                </FormControl>

                {/* Angle Tolerance */}
                <ToleranceInputGroup
                  hasToleranceFieldName="angleHasTolerance"
                  hasTolerance={values.angleHasTolerance}
                  onHasToleranceChange={(checked) =>
                    setFieldValue('angleHasTolerance', checked)
                  }
                  toleranceTypeFieldName="angleToleranceType"
                  toleranceType={values.angleToleranceType}
                  onToleranceTypeChange={(type) =>
                    setFieldValue('angleToleranceType', type)
                  }
                  symmetricFieldName="angleToleranceSymmetric"
                  symmetricValue={values.angleToleranceSymmetric}
                  onSymmetricChange={(value) =>
                    setFieldValue('angleToleranceSymmetric', value)
                  }
                  upperFieldName="angleToleranceUpper"
                  upperValue={values.angleToleranceUpper}
                  onUpperChange={(value) =>
                    setFieldValue('angleToleranceUpper', value)
                  }
                  lowerFieldName="angleToleranceLower"
                  lowerValue={values.angleToleranceLower}
                  onLowerChange={(value) =>
                    setFieldValue('angleToleranceLower', value)
                  }
                  errors={errors}
                  touched={touched}
                  checkboxLabel="Include angle tolerance"
                  toleranceLabel="Angle Tolerance Type"
                  symmetricLabel="Angle Tolerance (±)"
                  upperLabel="Angle Upper Tolerance"
                  lowerLabel="Angle Lower Tolerance"
                  max={180}
                />
              </>
            )}

            {/* Multiplier */}
            <MultiplierInput
              hasMultFieldName="hasMult"
              hasMult={values.hasMult}
              onHasMultChange={(checked) => setFieldValue('hasMult', checked)}
              multFieldName="mult"
              multValue={values.mult}
              onMultChange={(value) => setFieldValue('mult', value)}
              errors={errors}
              touched={touched}
            />
          </Flex>
        </SubFormWrapper>
      )}
    </Formik>
  );
};
